/**
 * @file ToggleComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de Toggle
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-02-27
 * @modified 2025-08-27
 * @author DLTCode
 * @author Eva Tellez
 * @author Nacho Moya
 * @author Pedro Luis Gómez
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { useTranslations } from "next-intl";
import { ToggleProps } from "@/types/ui";
import clsx from "clsx";

const ToggleComponent: React.FC<ToggleProps> = ({
  id,
  name,
  checked,
  onChange,
  className,
  label,
  hideLabel,
  ariaLabelKey,
  labelPosition = "right",
  readonly = false,
  "aria-label": ariaLabelProp,
  "aria-labelledby": ariaLabelledByProp,
}) => {
  const t = useTranslations("ToggleComponent");
  const aria = useTranslations("Aria.Toggle");
  const actionText = checked ? t("deactivateAction") : t("activeAction");
  const stateText = checked ? t("active") : t("deactivate");
  const labelText = readonly ? stateText : (label ?? actionText);
  const inputId = id || `toggle-${name}`;
  const computedAriaLabel =
    ariaLabelProp ??
    (ariaLabelledByProp
      ? undefined
      : ariaLabelKey
        ? aria(ariaLabelKey, {
            checked: String(checked),
          })
        : undefined);
  const hasAssociatedLabel = Boolean(label);

  const handleToggle = () => {
    if (!readonly) {
      onChange(!checked);
    }
  };

  return (
    <article
      className={clsx(
        "toggle__container",
        className,
        labelPosition === "right" ? "toggle__container--label-right" : ""
      )}
    >
      {label && (
        <label
          htmlFor={inputId}
          className={clsx(
            "toggle__label",
            readonly && "toggle__label--disabled",
            hideLabel && "sr-only"
          )}
        >
          {label ?? labelText}
        </label>
      )}

      <button
        className={clsx("toggle", readonly && "toggle--disabled")}
        role="switch"
        aria-checked={checked}
        aria-label={computedAriaLabel}
        aria-labelledby={ariaLabelledByProp}
        onClick={handleToggle}
        type="button"
        tabIndex={readonly ? -1 : 0}
        disabled={readonly}
      >
        <input
          id={inputId}
          name={name}
          type="checkbox"
          aria-label={hasAssociatedLabel ? undefined : computedAriaLabel}
          aria-labelledby={
            hasAssociatedLabel ? undefined : ariaLabelledByProp
          }
          checked={checked}
          onChange={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          disabled={readonly}
          tabIndex={-1}
        />

        <span className="toggle__slider"></span>
      </button>
    </article>
  );
};

export default ToggleComponent;
